import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';
import { getImageUrl } from '@/utils/images';
import { getTailwindColor } from '@/utils/colors';
import { useLocalization } from '@/hooks/use-localization';
import { NavButton } from '../carousel/components/nav-button';

type QuoteSlide = {
    quote: string;
    author: string;
    role: string;
    image?: {
        __isimage: boolean;
        src: string;
        alt_text: string;
        originalSize: {
            width: number;
            height: number;
        };
        focus: {
            x: number;
            y: number;
        };
    };
};

export type QuoteSliderProps = {
    slides: QuoteSlide[];
    textColor?: string;
    quoteMarkColor?: string;
    useLargeCorner?: boolean;
    largeCornerPositions?: Array<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>;
};

export const QuoteSlider = ({
    slides = [],
    textColor = 'grayDark',
    quoteMarkColor = 'primaryAlt',
    useLargeCorner = false,
    largeCornerPositions = [],
}: QuoteSliderProps) => {
    const { localize } = useLocalization();
    const sliderRef = useRef<HTMLUListElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const isFirstSlide = activeIndex === 0;
    const isLastSlide = activeIndex === slides.length - 1;
    const hasMultipleSlides = slides.length > 1;

    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider) return;

        const handleScroll = () => {
            const newIndex = Math.round(slider.scrollLeft / slider.offsetWidth);
            setActiveIndex(newIndex);
        };

        slider.addEventListener('scroll', handleScroll);
        return () => slider.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSlide = (index: number) => {
        const slider = sliderRef.current;
        if (!slider) return;
        slider.scrollTo({ left: index * slider.offsetWidth, behavior: 'smooth' });
    };

    const goToPrev = () => !isFirstSlide && scrollToSlide(activeIndex - 1);
    const goToNext = () => !isLastSlide && scrollToSlide(activeIndex + 1);

    if (!slides.length) {
        return null;
    }

    const navButtonClassName =
        'r-flex r-h-10 r-w-10 r-items-center r-justify-center r-rounded-full r-bg-white r-shadow-[0_2px_4px_rgba(0,0,0,0.3)] disabled:r-opacity-50';

    return (
        <div data-component="quote-slider" className="r-max-w-full r-overflow-hidden">
            <ul
                ref={sliderRef}
                role="list"
                aria-label={localize('quote-slider-list')}
                tabIndex={-1}
                className="scrollbar-hide r-flex r-snap-x r-snap-mandatory r-gap-4 r-overflow-x-auto r-pb-4"
            >
                {slides.map((slide, index) => (
                    <li key={index} className="r-flex r-w-full r-min-w-full r-snap-start r-flex-col r-gap-4 md:r-grid md:r-grid-cols-2 md:r-gap-8">
                        {slide.image?.src ? (
                            <img
                                src={getImageUrl(slide.image.src)}
                                alt={slide.image.alt_text || ''}
                                style={{
                                    objectPosition:
                                        typeof slide.image.focus?.x === 'number' && typeof slide.image.focus?.y === 'number'
                                            ? `${slide.image.focus.x * 100}% ${slide.image.focus.y * 100}%`
                                            : undefined,
                                }}
                                className={clsx('r-block r-aspect-[3/2] r-w-full r-object-cover', {
                                    'r-rounded-3xl': !useLargeCorner,
                                    'r-rounded-tl-[90px]': useLargeCorner && largeCornerPositions?.includes('top-left'),
                                    'r-rounded-tr-[90px]': useLargeCorner && largeCornerPositions?.includes('top-right'),
                                    'r-rounded-bl-[90px]': useLargeCorner && largeCornerPositions?.includes('bottom-left'),
                                    'r-rounded-br-[90px]': useLargeCorner && largeCornerPositions?.includes('bottom-right'),
                                })}
                            />
                        ) : (
                            <div />
                        )}
                        <div
                            className={clsx('r-flex r-flex-col r-p-4 md:r-p-6 md:r-pr-12', getTailwindColor(textColor, 'text'), {
                                'r-basis-1/2': slide.image?.src,
                            })}
                        >
                            <p
                                className={clsx(
                                    'r-m-0 -r-mb-6 r-font-montserrat r-text-[9rem] r-font-bold r-italic r-leading-[0.75]',
                                    getTailwindColor(quoteMarkColor, 'text')
                                )}
                            >
                                &quot;
                            </p>
                            <p className="r-mb-4 r-text-2xl r-font-bold" dangerouslySetInnerHTML={{ __html: slide.quote }} />
                            <p className="r-m-0 r-font-semibold" dangerouslySetInnerHTML={{ __html: slide.author }} />
                            <p className="r-m-0 r-text-sm" dangerouslySetInnerHTML={{ __html: slide.role }} />
                        </div>
                    </li>
                ))}
            </ul>

            {hasMultipleSlides && (
                <div className="r-relative r-flex r-items-center r-justify-between r-pb-6 r-pt-4 md:r-pt-8">
                    <div className="r-flex r-flex-1 r-justify-center r-gap-2">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                aria-hidden="true"
                                tabIndex={-1}
                                onClick={() => scrollToSlide(index)}
                                className={clsx(
                                    'r-h-2.5 r-rounded-full r-p-0 r-transition-all',
                                    activeIndex === index
                                        ? 'r-w-8 r-border-2 r-border-gray-500 r-bg-transparent'
                                        : 'r-w-2.5 r-border-0 r-bg-gray-500 hover:r-bg-gray-600'
                                )}
                            />
                        ))}
                    </div>
                    <div className="r-absolute r-bottom-0 r-right-0 r-flex r-gap-2 r-p-2">
                        <NavButton
                            direction="prev"
                            onClick={goToPrev}
                            disabled={isFirstSlide}
                            label={localize('carousel-prev')}
                            className={navButtonClassName}
                        />
                        <NavButton
                            direction="next"
                            onClick={goToNext}
                            disabled={isLastSlide}
                            label={localize('carousel-next')}
                            className={navButtonClassName}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
